import i18n from '@/languages/i18n'
import { DEV_BRAND_OVERRIDE_KEY, DEV_BRAND_QUERY_KEY, getBrandThemeTokens, normalizeBrandValue, type ThemeBrand } from './theme'
import { HelperService } from '@/service/helper/helper'
import { STORAGE_KEYS } from '@libs/define/storage_key'
import { canShowDevUi, getDevElectronOverride, isElectron, setDevElectronOverride } from '@libs/utils/envUtils'
import { ref } from 'vue'
const { t } = i18n.global

const setTitleAndStyle = (brand: Brand) => {
  document.body.setAttribute('brand', brand)
  document.documentElement.setAttribute('brand', brand)

  const tokens = getBrandThemeTokens(brand as ThemeBrand)
  Object.entries(tokens).forEach(([token, value]) => {
    const themeValue = String(value)
    document.documentElement.style.setProperty(token, themeValue)
    document.body.style.setProperty(token, themeValue)
  })

  const link = document.getElementById('favicon') as HTMLLinkElement
  switch (brand) {
    case Brand.iyx:
      if (link) {
        link.href = '/iyx_favicon.ico'
      }
      document.title = `${t('IYX')}${t('utility')}`
      break
    case Brand.irok:
      if (link) {
        link.href = '/favicon.ico'
      }
      document.title = `${t('IROK')}${t('utility')}`
      break
    case Brand.carotmas:
      if (link) {
        link.href = '/carotmas.ico'
      }
      document.title = `${t('CAROTMAS')}${t('utility')}`
      break
  }
}

export enum Brand {
  iyx = 'IYX',
  irok = 'IROK',
  carotmas = 'CAROTMAS',
}
const brandWebsites: Record<Brand, string> = {
  [Brand.iyx]: 'www.iyx.fun',
  [Brand.irok]: 'www.irok.cn',
  [Brand.carotmas]: 'www.carotmas.com',
}
const brandTitles: Record<Brand, string> = {
  [Brand.iyx]: 'IYX',
  [Brand.irok]: 'IROK',
  [Brand.carotmas]: 'CAROTMAS',
}

export const canShowDevBrandSwitcher = () => {
  return canShowDevUi()
}

export const getDevBrandOverride = () => {
  if (!canShowDevUi()) {
    return null
  }

  const overrideBrand = normalizeBrandValue(localStorage.getItem(DEV_BRAND_OVERRIDE_KEY))
  return overrideBrand ? (overrideBrand as Brand) : null
}

export const setDevBrandOverride = async (value?: string | null) => {
  if (!canShowDevUi()) {
    return refreshBrand()
  }

  const nextBrand = normalizeBrandValue(value)
  if (nextBrand) {
    localStorage.setItem(DEV_BRAND_OVERRIDE_KEY, nextBrand)
  } else {
    localStorage.removeItem(DEV_BRAND_OVERRIDE_KEY)
  }

  return refreshBrand()
}

const resolveDevBrandOverride = (): Brand | null => {
  if (!canShowDevUi()) {
    return null
  }

  const queryBrand = normalizeBrandValue(new URLSearchParams(window.location.search).get(DEV_BRAND_QUERY_KEY))
  if (queryBrand) {
    localStorage.setItem(DEV_BRAND_OVERRIDE_KEY, queryBrand)
    return queryBrand as Brand
  }

  const queryAction = new URLSearchParams(window.location.search).get(DEV_BRAND_QUERY_KEY)?.trim().toLowerCase()
  if (queryAction === 'auto' || queryAction === 'clear') {
    localStorage.removeItem(DEV_BRAND_OVERRIDE_KEY)
    return null
  }

  const storedOverride = normalizeBrandValue(localStorage.getItem(DEV_BRAND_OVERRIDE_KEY))
  return storedOverride ? (storedOverride as Brand) : null
}

const attachBrandDebugHelpers = () => {
  if (!canShowDevUi()) {
    return
  }

  const debugWindow = window as typeof window & {
    __getDriverBrand?: () => { active: Brand; override: string | null }
    __setDriverBrand?: (value?: string | null) => Promise<Brand>
    __getDriverElectron?: () => { active: boolean; override: boolean | null }
    __setDriverElectron?: (value?: boolean | null) => Promise<{ electron: boolean; brand: Brand }>
  }

  debugWindow.__getDriverBrand = () => ({
    active: brand.value,
    override: localStorage.getItem(DEV_BRAND_OVERRIDE_KEY),
  })

  debugWindow.__setDriverBrand = setDevBrandOverride
  debugWindow.__getDriverElectron = () => ({
    active: isElectron(),
    override: getDevElectronOverride(),
  })
  debugWindow.__setDriverElectron = async (value?: boolean | null) => {
    const electron = setDevElectronOverride(value)
    const nextBrand = await refreshBrand()
    return {
      electron,
      brand: nextBrand,
    }
  }
}

export const refreshBrand = async (): Promise<Brand> => {
  const resolvedBrand = await getBrand()
  brand.value = resolvedBrand
  setTitleAndStyle(resolvedBrand)
  return resolvedBrand
}

export const getBrandTitle = async (): Promise<string> => {
  const brand = await getBrand()
  return brandTitles[brand]
}
export const getBrandWebsite = async (): Promise<string> => {
  const brand = await getBrand()
  return brandWebsites[brand]
}

export const getBrand = async (): Promise<Brand> => {
  const devOverride = resolveDevBrandOverride()
  if (devOverride) {
    return devOverride
  }

  if (isElectron()) {
    const brand = localStorage.getItem(STORAGE_KEYS.BRAND)
    if (brand) return brand as Brand

    const helperService = HelperService.instance
    return new Promise((resolve) => {
      helperService.callbackOfBrand((bd: Brand) => {
        localStorage.setItem(STORAGE_KEYS.BRAND, bd)
        resolve(bd)
      })
    })
  }
  if (window.location.host.includes('iyx')) {
    return Brand.iyx
  } else if (window.location.host.includes('carotmas')) {
    return Brand.carotmas
  } else {
    return Brand.irok
  }
}

export const brand = ref(Brand.irok)

attachBrandDebugHelpers()

refreshBrand()
